import { useEffect, useRef } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Grid,
  Input,
  SelectForm,
  Picker,
  Form,
  Line,
  InputMask
} from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import moment from 'moment'
import {
  Entity,
  ResponseGetParametrizacaoEfeitvo
} from 'pages/Supplier/MovMensal/types'
import Draggable from 'react-draggable'
import { useForm } from 'react-hook-form'
import { FORMAT, SPACING } from 'utils'
import { isValidCPF } from 'utils/functions'
import * as Yup from 'yup'

import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled
} from './ModalNovaAddEfetivo.styles'

const DEFAULT_FORM = {
  cdCategoria: '',
  cdCpfPassaporte: '',
  cdGenero: '',
  cdNacionalidade: '',
  cdStatus: '',
  cdTipoEfetivo: '',
  dtAdmissao: null,
  dtDemissao: null,
  nmColaborador: '',
  nuMesAnoCompetencia: '',
  txFuncao: '',
  vlHoraExtras: undefined,
  vlSalBase: undefined
} as Entity

export interface IModalNovaAddEfetivo {
  data?: any
  showModal: boolean
  competenciaAtual: string
  toggleModal: () => void
  onSubmit: (_data, _id?) => void
  dadosParametrizacao: ResponseGetParametrizacaoEfeitvo | undefined
  listaAtual: Entity[]
}

const ModalNovaAddEfetivo: React.FC<IModalNovaAddEfetivo> = ({
  data,
  showModal,
  toggleModal,
  onSubmit,
  competenciaAtual,
  dadosParametrizacao,
  listaAtual
}) => {
  const DEFAULT_VALUES_WHEN_EDIT = {
    cdCategoria: data ? data.cdCategoria?.trim() : '',
    cdCpfPassaporte: data ? data.cdCpfPassaporte : '',
    cdGenero: data ? data.cdGenero : '',
    cdNacionalidade: data ? data.cdNacionalidade?.trim() : '',
    cdStatus: data ? data.cdStatus?.trim().toUpperCase() : '',
    cdTipoEfetivo: data ? data.cdTipoEfetivo?.trim() : '',
    dtAdmissao: data ? data.dtAdmissao : null,
    dtDemissao: data ? data.dtDemissao : null,
    nmColaborador: data ? data.nmColaborador : '',
    nuMesAnoCompetencia: data ? data.nuMesAnoCompetencia : '',
    txFuncao: data ? data.txFuncao : '',
    vlHoraExtras: data ? data.vlHoraExtras : undefined,
    vlSalBase: data ? data.vlSalBase : undefined
  } as Entity

  const schema = Yup.object({
    cdCategoria: Yup.string().required('Campo obrigatório'),
    cdCpfPassaporte: Yup.string()
      .required('Campo obrigatório')
      .test('cdCpfPassaporte', 'CPF/Passaporte inválido.', (values) => {
        const refValue = FORMAT.unMask(values)
        if (isNaN(refValue)) return true
        return isValidCPF(refValue)
      })
      .test('cdCpfPassaporte', 'CPF/Passaporte inválido', (value) => {
        let isDuplicated = false
        if (data) {
          isDuplicated = listaAtual
            .filter(
              (x) =>
                FORMAT.unMask(x.cdCpfPassaporte.trim()) !==
                FORMAT.unMask(data?.cdCpfPassaporte.trim())
            )
            .some(
              (el) =>
                FORMAT.unMask(el.cdCpfPassaporte.trim()) ===
                FORMAT.unMask(value.trim())
            )
        } else {
          isDuplicated = listaAtual.some(
            (el) =>
              FORMAT.unMask(el.cdCpfPassaporte.trim()) ===
              FORMAT.unMask(value.trim())
          )
        }
        if (isDuplicated) return false
        else return true
      }),
    cdGenero: Yup.string().required('Campo obrigatório'),
    cdNacionalidade: Yup.string().required('Campo obrigatório'),
    cdStatus: Yup.string().required('Campo obrigatório'),
    cdTipoEfetivo: Yup.string().required('Campo obrigatório'),
    dtAdmissao: Yup.date().when('cdStatus', (status) => {
      if (status === 'DEMITIDO') {
        return Yup.date()
          .typeError('Data inválida')
          .required('Campo obrigatório')
          .test('admission-after-today', 'Data inválida', (date) =>
            moment().isAfter(date)
          )
          .max(Yup.ref('dtDemissao'), 'Deve ser antecedente a data de demissão')
          .nullable()
      }
      return Yup.date()
        .typeError('Data inválida')
        .required('Campo obrigatório')
        .test('admission-after-today', 'Data inválida', (date) =>
          moment().isAfter(date)
        )
        .nullable()
    }),
    dtDemissao: Yup.date().when('cdStatus', (status) => {
      if (status === 'DEMITIDO') {
        return Yup.date()
          .typeError('Data inválida')
          .required('Campo obrigatório')
          .nullable()
          .test('resignation-after-today', 'Data inválida', (date) =>
            moment().isAfter(date)
          )
          .min(Yup.ref('dtAdmissao'), 'Deve ser posterior a data de admissão')
      }
      return Yup.date().nullable()
    }),
    nmColaborador: Yup.string()
      .trim()
      .required('Campo obrigatório')
      .min(11, "O nome do colaborador deve ser maior ou igual a 11 caracteres.")
      .test('nmColaborador', 'Nome inválido.', (value) => {
        return /^[ a-zA-ZÀ-ÿ\u00f1\u00d1]*$/g.test(value ?? '')
      })
      .test('no-double-spaces', 'O nome não pode conter espaços duplos', (value) => {
        return !/\s{2,}/.test(value ?? '')
      })
      .matches(/^(?!\s*$)[ a-zA-ZÀ-ÿ\u00f1\u00d1]*$/, 'Nome inválido.')
    ,
    nuMesAnoCompetencia: Yup.string().required('Campo obrigatório'),
    txFuncao: Yup.string()
      .matches(/^(?!\s*$)[a-zA-ZÀ-ÿ\s]*$/, 'Função deve conter apenas letras.')
      .required('Campo obrigatório'),
    ...(dadosParametrizacao?.flValorHeSalarioBasePorColaborador && {
      vlHoraExtras: Yup.string()
        .test('cant-convert', 'Valor inválido', (value) => {
          const valueNumber = +value
          if (typeof valueNumber !== 'number' || isNaN(valueNumber))
            return false
          return true
        })
        .test('less-than-zero', 'Valor não pode ser negativo', (value) => {
          const valueNumber = +value
          if (valueNumber < 0) return false
          return true
        })
        .required('Campo obrigatório'),
      vlSalBase: Yup.string()
        .test('cant-convert', 'Valor inválido', (value) => {
          const valueNumber = +value
          if (typeof valueNumber !== 'number' || isNaN(valueNumber))
            return false
          return true
        })
        .test('less-than-zero', 'Valor não pode ser negativo', (value) => {
          const valueNumber = +value
          if (valueNumber < 0) return false
          return true
        })
        .required('Campo obrigatório')
    })
  })

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    formState: { errors },
    watch,
    setValue
  } = useForm<Entity>({
    defaultValues: data
      ? { ...DEFAULT_VALUES_WHEN_EDIT, nuMesAnoCompetencia: competenciaAtual }
      : { ...DEFAULT_FORM, nuMesAnoCompetencia: competenciaAtual },
    resolver: yupResolver(schema)
  })

  const watchStatus = watch('cdStatus')
  const nacionalidadeWatcher = watch('cdNacionalidade')

  const isBrasileiro = (): boolean => nacionalidadeWatcher.toString() === 'B'

  const handleResetForm = () => {
    reset()
  }

  useEffect(() => {
    const subs = watch((ref, { name }) => {
      if (name === 'cdCpfPassaporte') {
        clearErrors('cdCpfPassaporte')
        let isDuplicated = false

        if (data) {
          isDuplicated = listaAtual
            .filter(
              (x) =>
                FORMAT.unMask(x.cdCpfPassaporte.trim()) !==
                FORMAT.unMask(data?.cdCpfPassaporte.trim())
            )
            .some(
              (el) =>
                FORMAT.unMask(el.cdCpfPassaporte.trim()) ===
                FORMAT.unMask(ref[name].trim())
            )
        } else {
          isDuplicated = listaAtual.some(
            (el) =>
              FORMAT.unMask(el.cdCpfPassaporte.trim()) ===
              FORMAT.unMask(ref[name].trim())
          )
        }

        if (isDuplicated) {
          setError('cdCpfPassaporte', {
            message: 'Colaborador já cadastrado'
          })
          return
        }
      }

      if (name === 'cdNacionalidade') {
        setValue('cdCpfPassaporte', '')
      }

      if (name === 'cdStatus') {
        if (ref[name] !== 'Demitido') {
          setValue('dtDemissao', null)
        }
      }
    })

    return () => subs.unsubscribe()
  }, [watch])

  const draggableReference = useRef<Draggable>(null)

  return (
    <DraggableDialog
      reference={draggableReference}
      showModal={showModal}
      onClose={toggleModal}
      width={'lg'}
    >
      <Line />
      <Form
        onSubmit={handleSubmit((data) => {
          onSubmit({
            ...data,
            ...(data.dtDemissao && { dtDemissao: data.dtDemissao })
          })
          toggleModal()
          resetModalState(draggableReference)
        })}
      >
        <DialogTitleStyled>{data ? 'Editar' : 'Novo'}</DialogTitleStyled>
        <DialogContentStyled>
          <Grid
            gridCols="150px 316px 150px 1fr 150px"
            gridHeight="auto"
            gridColsMobile="1fr 1fr"
            gridGap="1rem"
            margin="0 0 1rem 0"
            alignItems="center"
          >
            <SelectForm
              control={control}
              errors={errors}
              listOptions={[
                { label: 'Brasileiro', value: 'B' },
                { label: 'Estrangeiro', value: 'E' }
              ]}
              label="Nacionalidade"
              formValue="cdNacionalidade"
            />
            <Input
              label={isBrasileiro() ? 'CPF' : 'Passaporte'}
              control={control}
              errors={errors}
              placeholder={isBrasileiro() ? 'CPF' : 'Passaporte'}
              mask={isBrasileiro() ? ['###.###.###-##'] : null}

              maxLength={isBrasileiro() ? 14 : 13}
              formValue="cdCpfPassaporte"
              required
            />
            <SelectForm
              control={control}
              errors={errors}
              listOptions={[
                { label: 'Masculino', value: 'M' },
                { label: 'Feminino', value: 'F' },
                { label: 'Prefiro não informar', value: 'O' }
              ]}
              label="Gênero"
              formValue="cdGenero"
            />

            <InputMask
              type="letter"
              label="Nome"
              control={control}
              errors={errors}
              placeholder="Nome"
              formValue="nmColaborador"
              minLength={11}
              required

            />

            <SelectForm
              control={control}
              errors={errors}
              listOptions={[
                { label: 'ATIVO', value: 'ATIVO' },
                { label: 'DEMITIDO', value: 'DEMITIDO' },
                { label: 'ACESSO', value: 'ACESSO' },
                { label: 'TRANSFERIDO', value: 'TRANSFERIDO' },
                { label: 'AFASTADO', value: 'AFASTADO' },
                { label: 'COMPARTILHADO', value: 'COMPARTILHADO' }
              ]}
              label="Status"
              formValue="cdStatus"
              defaultValue={data ? data.cdStatus : null}
            />
          </Grid>
          <Grid
            gridCols="1fr 1fr 1fr 1fr 1fr 1fr 1fr"
            gridHeight="auto"
            gridColsMobile="1fr 1fr"
            gridGap="1rem"
            margin="0 0 1rem 0"
          >
            <Picker
              label="Data Admissão"
              control={control}
              errors={errors}
              placeholder="Data Admissão"
              defaultValue={null}
              formValue="dtAdmissao"
              maxDate={moment()}
            />
            {watchStatus === 'DEMITIDO' && (
              <Picker
                label="Data Demissão"
                control={control}
                errors={errors}
                placeholder="Data Demissão"
                defaultValue={null}
                formValue="dtDemissao"
                maxDate={moment()}
              />
            )}

            <SelectForm
              control={control}
              errors={errors}
              listOptions={[
                { label: 'CLT', value: 'CLT' },
                { label: 'Sócio', value: 'Socio' },
                { label: 'Cooperado', value: 'Cooperado' },
                { label: 'Estrangeiro', value: 'Estrangeiro' },
                { label: 'Estagiário', value: 'Estagiario' },
                { label: 'Autônomo', value: 'Autonomo' },
                { label: 'MEI', value: 'MEI' }
              ]}
              label="Tipo Efetivo"
              formValue="cdTipoEfetivo"
            />

            <InputMask
              type="alfanumeric"
              label="Função"
              control={control}
              errors={errors}
              maxLength={50}
              placeholder="Função"
              formValue="txFuncao"
              required
            />

            <SelectForm
              control={control}
              errors={errors}
              listOptions={[
                { label: 'Administrativo', value: 'A' },
                { label: 'Operacional', value: 'O' }
              ]}
              label="Categoria"
              formValue="cdCategoria"
            />

            <Input
              label="Competência"
              placeholder="Competência"
              value={FORMAT.competencia(competenciaAtual)}
              disabled
            />
            {dadosParametrizacao?.flValorHeSalarioBasePorColaborador && (
              <InputMask
                label="Valor Hora Extra"
                control={control}
                errors={errors}
                mask={'9'.repeat(20)}
                placeholder="Valor Hora Extra"
                formValue="vlHoraExtras"
                required
              />
            )}
            {dadosParametrizacao?.flValorHeSalarioBasePorColaborador && (
              <InputMask
                label="Salário Base"
                mask={'9'.repeat(20)}
                control={control}
                errors={errors}
                placeholder="Salário Base"
                formValue="vlSalBase"
                required
              />
            )}
          </Grid>
        </DialogContentStyled>
        <DialogActionsStyled>
          {!data && (
            <Button
              text="Limpar"
              variantButton="text"
              marginRight={SPACING.spacing20}
              onClick={handleResetForm}
            />
          )}
          <Button
            text="Voltar"
            variantButton="outlined"
            marginRight={SPACING.spacing20}
            onClick={() => {
              toggleModal()
              resetModalState(draggableReference)
            }}
          />
          <Button text="Salvar" type="submit" />
        </DialogActionsStyled>
      </Form>
    </DraggableDialog>
  )
}

export default ModalNovaAddEfetivo
